import { motion, useReducedMotion } from 'motion/react';
import { useTheme } from 'next-themes';
import { type CSSProperties, type FocusEvent, memo, type RefObject } from 'react';

import { WorkspaceAgentSpaceFabMark } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/WorkspaceAgentSpaceFabMark';

export type WorkspaceAgentIdleFabProps = {
  buttonRef: RefObject<HTMLButtonElement>;
  ctaTransitionStyle: CSSProperties;
  isExpanded: boolean;
  onBlur: () => void;
  onFocus: (event: FocusEvent<HTMLButtonElement>) => void;
  onOpen: () => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
};

/**
 * Pre-ui2026 idle agent entry. Circle that expands to "Start task" on hover/focus; no bar morph.
 */
export const WorkspaceAgentIdleFab = memo(
  ({
    buttonRef,
    ctaTransitionStyle,
    isExpanded,
    onBlur,
    onFocus,
    onOpen,
    onPointerEnter,
    onPointerLeave,
  }: WorkspaceAgentIdleFabProps) => {
    const { resolvedTheme } = useTheme();
    const shouldReduceMotion = useReducedMotion();

    return (
      <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }}>
        <button
          aria-label="Start task"
          className="group pointer-events-auto relative flex h-12 w-12 shrink-0 items-center justify-start rounded-full bg-gray-12 px-3 text-gray-1 shadow-m ring-2 ring-white/20 focus-visible:outline-none focus-visible:ring-altitude-8 active:scale-95 data-[expanded]:w-36 data-[expanded]:pr-4 data-[expanded]:text-white data-[expanded]:shadow-popover data-[expanded]:ring-white/30 dark:ring-black/10 dark:data-[expanded]:ring-white/30"
          data-expanded={isExpanded ? '' : undefined}
          data-testid="PRIVATE_WORKSPACE_AGENT_FAB"
          onBlur={onBlur}
          onClick={onOpen}
          onFocus={onFocus}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          ref={buttonRef}
          style={ctaTransitionStyle}
          type="button"
        >
          <WorkspaceAgentSpaceFabMark
            isDarkTheme={resolvedTheme === 'dark'}
            isExpanded={isExpanded}
            shouldReduceMotion={!!shouldReduceMotion}
          />
        </button>
      </motion.div>
    );
  },
);

WorkspaceAgentIdleFab.displayName = 'WorkspaceAgentIdleFab';
